// 牌效評估工具
// 給 AI 出牌策略用的 helpers，計算每張牌的「該打程度」。
//
// 為什麼分檔？
// 評估邏輯獨立後，未來可以重用（例：UI 顯示「建議打」、AI 換策略）。
// 也方便單元測試各個小規則。

import type { THand } from '@core/hand';
import { isHonor, isNumeric, isSameKind, type TTile } from '@core/tile';

/**
 * 統計手中除了某張牌之外，有多少張同種牌。
 * （用於判斷是否是孤張、對子、刻子）
 */
export const countSameKindExcluding = (hand: THand, target: TTile): number =>
  hand.concealed.filter((t) => t.id !== target.id && isSameKind(t, target)).length;

/**
 * 同 suit 內距離 target 的鄰居張數（rank 差 1 或 2）。
 * 回傳 { adjacent, twoAway }：
 *   - adjacent: rank 差 1 的不同 id 同 suit 牌數
 *   - twoAway: rank 差 2 的不同 id 同 suit 牌數
 *
 * 用途：
 * - adjacent + 自己 = 兩面搭子或順子的一部分
 * - twoAway + 自己 = 嵌張搭子的一部分（rank-2, rank, rank+2 中間缺）
 */
export const countNumericNeighbors = (
  hand: THand,
  target: TTile
): { adjacent: number; twoAway: number } => {
  if (!isNumeric(target)) return { adjacent: 0, twoAway: 0 };
  let adjacent = 0;
  let twoAway = 0;
  for (const t of hand.concealed) {
    if (t.id === target.id) continue;
    if (t.suit !== target.suit) continue;
    const diff = Math.abs(t.rank - target.rank);
    if (diff === 1) adjacent++;
    else if (diff === 2) twoAway++;
  }
  return { adjacent, twoAway };
};

/**
 * 是否在「已成順子」中（手牌包含完整連續三張，target 是其中之一）。
 *
 * 檢查三種型態：
 *   (target-2, target-1, target)
 *   (target-1, target, target+1)
 *   (target, target+1, target+2)
 *
 * 排除 target 本身（id 比對），確保不會用同一張牌自證。
 */
export const isInCompletedRun = (hand: THand, target: TTile): boolean => {
  if (!isNumeric(target)) return false;
  const has = (rank: number): boolean =>
    hand.concealed.some(
      (t) => t.id !== target.id && t.suit === target.suit && t.rank === rank
    );
  const r = target.rank;
  return (
    (r >= 3 && has(r - 1) && has(r - 2)) ||
    (r >= 2 && r <= 8 && has(r - 1) && has(r + 1)) ||
    (r <= 7 && has(r + 1) && has(r + 2))
  );
};

/**
 * 計算單張牌的「該打程度」。分數越高代表越該打。
 *
 * 規則優先序（PRD §7.2，已轉換為連續分數）：
 * - 已成刻子的成員：-100（強烈不打）
 * - 已成對子的成員：-50（不拆對子）
 * - 已成順子的成員：-100（不拆順子）
 * - 兩面/嵌張搭子的成員：-30（不拆搭子）
 * - 邊張數牌（1/9）且鄰居很弱：+60
 * - 完全孤張數牌（無 ±1 ±2 鄰居）：+90
 * - 孤張字牌：+100（最該打）
 * - 其他（中間張、有強鄰居）：0
 */
export const evaluateTileForDiscard = (hand: THand, target: TTile): number => {
  const sameKind = countSameKindExcluding(hand, target);

  // 已成刻子（手中還有 2 張以上同種）：絕對不拆
  if (sameKind >= 2) return -100;

  // 已成對子：避免拆
  if (sameKind === 1) return -50;

  // 此後 sameKind === 0，target 是孤張某種

  // 字牌孤張：最該打
  if (isHonor(target)) return 100;

  // 數牌孤張：依鄰居關係細分
  if (isNumeric(target)) {
    const { adjacent, twoAway } = countNumericNeighbors(hand, target);

    // 完全孤張（無 ±1 也無 ±2 鄰居）
    if (adjacent === 0 && twoAway === 0) return 90;

    // 在順子中：絕對別拆
    if (isInCompletedRun(hand, target)) return -100;

    // 邊張（1 或 9）且鄰居支援度低
    if ((target.rank === 1 || target.rank === 9) && adjacent === 0) return 60;

    // 兩面搭子（有 adjacent）或嵌張搭子（有 twoAway）：避免拆
    if (adjacent > 0 || twoAway > 0) return -30;
  }

  // fallback：理論不會到這（花牌不該出現於 concealed）
  return 0;
};
